import {
  ComponentProps,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import styled from '@emotion/styled';
import { FCWithChildren } from 'helpers/types/FCWithChildren';

import { CircularProgress } from '../icons/CircularProgress';
import { color, spacing, unit } from '../theme';

interface ButtonProps {
  variant?: 'primary' | 'secondary';
  link?: boolean;
  disabled?: boolean;
  disabledClear?: boolean;
}

export const Button = styled.button<ButtonProps>`
  appearance: none;
  min-width: 120px;
  padding: 0 ${spacing.xs};
  height: ${unit * 10}px;
  line-height: 1;
  border-radius: ${unit * 5}px;
  border: ${props =>
    `2px solid ${color(props.disabled ? 'lightGreyDark' : 'greyDark')(props)}`};
  box-shadow: ${props =>
    props.disabled ? 'none' : `0 ${unit}px 0 0 ${color('greyDark')(props)}`};

  ${props =>
    props.disabled
      ? `
        color: ${color('greyLight')(props)};
      `
      : `
    :hover {
      box-shadow: 0 ${unit * 1.5}px 0 0 ${color('greyDark')(props)};
      margin-top: -${unit * 0.5}px;
      margin-bottom: ${unit * 0.5}px
    }

    :active {
      box-shadow: none;
      margin-top: ${unit * 0.5}px;
      margin-bottom: -${unit * 0.5}px;
    }
  `}

  background-color: ${props =>
    props.variant === 'primary' && !props.disabled
      ? color('yellowMedium')
      : props.disabled && props.disabledClear
      ? 'transparent'
      : color('white')};

  transition:
    box-shadow 0.3s,
    margin-top 0.3s,
    margin-bottom 0.3s;
`;

Button.defaultProps = {
  variant: 'primary',
  link: false,
  disabled: false,
};

const _SubmitButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  > svg {
    margin-left: -34px;
  }
`;

export interface SubmitButtonProps extends ComponentProps<typeof Button> {
  isLoading?: boolean;
}

export const SubmitButton: FCWithChildren<SubmitButtonProps> = ({
  onClick,
  isLoading = false,
  disabled,
  children,
  ...rest
}) => {
  const ref = useRef<HTMLButtonElement>(null);
  const timeoutRef = useRef<number | NodeJS.Timeout | undefined>();

  const disabledOverride = isLoading || disabled;

  /**
   * It is possible to click the button faster than react will re-render state
   * To prevent double-click submissions, we manually set the button's disabled attribute
   *
   * However, not all clicks are guaranteed to set it (and thus un-set it)
   * For exmaple, clicking submit on a form that has validation errors will never
   * put it into a loading or disabled state, so we add a timer to un-disable the button
   *
   * Finally, we add a useEffect hook that will clear out that timer if a parent component
   * DOES update the loading or disabled states- we shouldn't clear out a manually set disabled state
   * if an API call takes awhile, for example
   */

  const clearManuallySetDisabled = () => {
    ref.current?.removeAttribute('disabled');
    timeoutRef.current = undefined;
  };

  const startDebounce = useCallback(() => {
    if (timeoutRef.current) return;
    timeoutRef.current = setTimeout(clearManuallySetDisabled, 750);
  }, []);

  const clearDebounce = useCallback(() => {
    if (!timeoutRef.current) return;

    // @ts-expect-error (set|clear)Timeout are overloaded and cannot be satisfied
    clearTimeout(timeoutRef.current);
    timeoutRef.current = undefined;
  }, []);

  const clickHandler = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      ref.current?.setAttribute('disabled', 'disabled');
      startDebounce();
      onClick?.(e);
    },
    [onClick, startDebounce],
  );

  useEffect(() => {
    // we shouldn't un-disable a button if new react state has overridden the
    // hack added to the click handler
    if (timeoutRef.current && (isLoading || disabled)) {
      clearDebounce();
    }
    return () => {
      timeoutRef.current && clearDebounce();
    };
  }, [clearDebounce, disabled, isLoading]);

  return (
    <_SubmitButton
      onClick={clickHandler}
      ref={ref}
      disabled={disabledOverride}
      {...rest}
    >
      {isLoading && <CircularProgress size={24} />}
      {children}
    </_SubmitButton>
  );
};
